import { Chip } from '@mui/material';
import React from 'react';

type Tone = 'default' | 'info' | 'success' | 'warning' | 'error';

const toneColor: Record<Tone, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  default: 'default',
  info: 'primary',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

export const formatStatusLabel = (label: string): string => label
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim();

export const StatusChip: React.FC<{ label: string; tone?: Tone }> = ({ label, tone = 'default' }) => (
  <Chip size="small" label={formatStatusLabel(label)} color={toneColor[tone]} variant="outlined" sx={{ fontWeight: 600 }} />
);

export function documentStatusTone(status?: string | null): Tone {
  switch (status) {
    case 'NeedsReview':
    case 'Unrecognised':
    case 'Unreadable':
      return 'warning';
    case 'Verified':
    case 'Extracted':
    case 'Classified':
      return 'success';
    case 'Failed':
    case 'DeadLettered':
    case 'Rejected':
      return 'error';
    case 'Extracting':
    case 'Queued':
    case 'Retrying':
    case 'Quarantined':
      return 'info';
    default:
      return 'default';
  }
}

export function fieldStatusTone(status?: string | null): Tone {
  switch (status) {
    case 'Verified':
      return 'success';
    case 'Corrected':
      return 'info';
    case 'NeedsReview':
      return 'warning';
    case 'Rejected':
      return 'error';
    default:
      return 'default';
  }
}

export function jobStatusTone(status?: string | null): Tone {
  switch (status) {
    case 'Succeeded':
      return 'success';
    case 'Running':
    case 'Pending':
      return 'info';
    case 'Failed':
    case 'DeadLettered':
      return 'error';
    default:
      return 'default';
  }
}

export function projectStatusTone(status?: string | null): Tone {
  switch (status) {
    case 'Active':
    case 'Published':
      return 'info';
    case 'Reported':
    case 'Closed':
      return 'success';
    case 'Draft':
      return 'default';
    case 'Merged':
      return 'warning';
    default:
      return 'default';
  }
}

export function confidenceTone(confidence?: number | null): Tone {
  if (confidence == null) {
    return 'default';
  }
  if (confidence >= 0.9) {
    return 'success';
  }
  if (confidence >= 0.7) {
    return 'warning';
  }
  return 'error';
}

export function formatConfidence(confidence?: number | null): string {
  if (confidence == null) {
    return '-';
  }
  return `${Math.round(confidence * 100)}%`;
}
